import { HotelBookingDto } from "../api/hotelBookingApi";

export function formatMoney(value: number) {
    return new Intl.NumberFormat("da-DK", {
        style: "currency",
        currency: "DKK",
    }).format(value || 0);
}

export function formatDate(value?: string | null) {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("da-DK");
}

export function formatDateTime(value?: string | null) {
    if (!value) return "-";
    return new Date(value).toLocaleString("da-DK");
}

export function toYmd(date: Date) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function getNights(checkIn: Date, checkOut: Date) {
    const start = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
    const end = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
    return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
}

export function getDaysUntil(date?: string | null) {
    if (!date) return null;

    const now = new Date();
    const target = new Date(date);
    const diff = target.getTime() - now.getTime();

    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function normalizeStatus(status?: string | null) {
    return (status || "").trim().replace(/\s+/g, "").toLowerCase();
}

export function getStatusColor(status: string) {
    switch (normalizeStatus(status)) {
        case "pendingapproval":
            return "yellow";
        case "awaitingpayment":
            return "blue";
        case "paid":
            return "green";
        case "roomassignmentopen":
            return "violet";
        case "completed":
            return "green";
        case "rejected":
        case "cancelled":
            return "red";
        default:
            return "gray";
    }
}

export function getStatusText(status: string, language: "en" | "da") {
    const isEn = language === "en";

    switch (normalizeStatus(status)) {
        case "pendingapproval":
            return isEn ? "Pending approval" : "Afventer godkendelse";
        case "awaitingpayment":
            return isEn ? "Awaiting payment" : "Afventer betaling";
        case "paid":
            return isEn ? "Paid" : "Betalt";
        case "roomassignmentopen":
            return isEn ? "Rooming list open" : "Rooming list åben";
        case "completed":
            return isEn ? "Completed" : "Afsluttet";
        case "rejected":
            return isEn ? "Rejected" : "Afvist";
        case "cancelled":
            return isEn ? "Cancelled" : "Annulleret";
        default:
            return status;
    }
}

export function getBookingStatusDescription(status: string, language: "en" | "da") {
    const isEn = language === "en";

    switch (normalizeStatus(status)) {
        case "pendingapproval":
            return isEn
                ? "Your booking has been submitted and is waiting for approval."
                : "Din booking er sendt og afventer godkendelse.";
        case "awaitingpayment":
            return isEn
                ? "Your booking has been approved. Please complete the payment."
                : "Din booking er godkendt. Gennemfør venligst betalingen.";
        case "paid":
            return isEn
                ? "Payment is registered. You can now fill in the rooming list."
                : "Betalingen er registreret. Du kan nu udfylde rooming list.";
        case "roomassignmentopen":
            return isEn
                ? "You can continue editing the rooming list."
                : "Du kan fortsætte med at redigere rooming list.";
        case "completed":
            return isEn
                ? "Booking and rooming list are completed."
                : "Booking og rooming list er afsluttet.";
        case "rejected":
            return isEn ? "This booking has been rejected." : "Denne booking er blevet afvist.";
        case "cancelled":
            return isEn ? "This booking has been cancelled." : "Denne booking er annulleret.";
        default:
            return isEn ? "Booking status is being processed." : "Bookingstatus behandles.";
    }
}

export function getRoomingProgress(booking: HotelBookingDto) {
    const bookingRooms = booking.rooms ?? [];

    const totalGuests = bookingRooms.reduce((sum, room) => {
        return sum + (room.guestsCount ?? 0);
    }, 0);

    const filledGuests = bookingRooms.reduce((sum, room) => {
        const filledInRoom = room.guests?.filter((guest) => guest.personName?.trim()).length ?? 0;
        return sum + filledInRoom;
    }, 0);

    return {
        totalGuests,
        filledGuests,
        missingGuests: Math.max(0, totalGuests - filledGuests),
    };
}

export function isRoomingDeadlinePassed(booking: HotelBookingDto) {
    if (!booking.roomingDeadline) return false;
    return new Date() > new Date(booking.roomingDeadline);
}

export function getDeadlineBadgeColor(daysLeft: number | null) {
    if (daysLeft === null) return "gray";
    if (daysLeft <= 0) return "red";
    if (daysLeft <= 7) return "yellow";
    return "green";
}

export function getDeadlineText(
    label: string,
    date: string | null | undefined,
    language: "en" | "da"
) {
    const daysLeft = getDaysUntil(date);

    if (daysLeft === null) return null;

    if (daysLeft <= 0) {
        return language === "en"
            ? `${label}: deadline passed`
            : `${label}: deadline overskredet`;
    }

    if (daysLeft === 1) {
        return language === "en"
            ? `${label}: 1 day left`
            : `${label}: 1 dag tilbage`;
    }

    return language === "en"
        ? `${label}: ${daysLeft} days left`
        : `${label}: ${daysLeft} dage tilbage`;
}
